import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';
import ClientsManagement from './admin/ClientsManagement';
import { MoreVertical, User, LogOut } from 'lucide-react';

export default function SuperAdminPanel() {
  const { t } = useTranslation();
  const { selectedClient } = useClient();
  const { currentUser, logout, userData } = useAuth();
  
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-dropdown') && showUserDropdown) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      // Error silenciado
    }
  };

  return (
    <div className="min-h-screen">
      {/* Barra de colores superior animada */}
      <div className="h-1 bg-gradient-to-r from-cyan-400 via-pink-500 via-purple-600 via-yellow-400 to-green-400 animate-gradient-x bg-[length:400%_400%]"></div>
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo de Pulpou arriba a la izquierda */}
            <div className="flex items-center space-x-4">
              <img 
                src="/logo_pulpou.svg" 
                alt="Pulpou" 
                className="h-8 w-auto"
              />
              {selectedClient && selectedClient.clientLogo && (
                <div className="pl-4 border-l border-gray-300">
                  {selectedClient.clientLogo.startsWith('http') ? (
                    <img 
                      src={selectedClient.clientLogo} 
                      alt={selectedClient.clientName}
                      className="h-8 w-auto object-contain"
                    />
                  ) : (
                    <div 
                      className="h-8 flex items-center justify-center"
                      dangerouslySetInnerHTML={{ __html: selectedClient.clientLogo }}
                    />
                  )}
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Menú de usuario */}
              <div className="relative user-dropdown">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center space-x-2 bg-white hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {t('common.hello')} {(userData?.name || currentUser?.email?.split('@')[0] || 'Usuario')
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(' ')}
                    </p>
                  </div>
                  <MoreVertical className="w-4 h-4 text-gray-400" />
                </button>

                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {(() => {
                          if (userData?.name && userData?.lastname) {
                            return `${userData.name} ${userData.lastname}`;
                          }
                          return (userData?.name || currentUser?.email?.split('@')[0] || 'Usuario');
                        })()
                          .split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {userData?.user_type === 'superadmin' ? t('admin.superadmin') : userData?.user_type === 'admin' ? t('admin.users.admin') : t('admin.users.user_type')}
                      </p>
                    </div>
                    <div className="p-1">
                      <button
                        onClick={() => {/* TODO: Implementar perfil */}}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center space-x-2 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        <span>{t('common.profile')}</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-3 py-2 text-sm text-[#EC4899] hover:bg-pink-50 rounded-md flex items-center space-x-2 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>{t('common.logout')}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content — el padding lo maneja el componente para no duplicar */}
      <ClientsManagement />
    </div>
  );
}